import type { CompareProduct } from './compare-events.js'

/** Single sessionStorage key — compare selection is shared across all PLPs and routes. */
const STORAGE_KEY = 'mosaic.csc-compare.selection'

function parseStoredArray(raw: string | null): CompareProduct[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item): item is CompareProduct =>
        item != null &&
        typeof item === 'object' &&
        typeof (item as CompareProduct).id === 'number',
    )
  } catch {
    return []
  }
}

/** Normalize pathname (kept for callers/tests; selection is no longer path-scoped). */
export function normalizeListingPath(pathname: string): string {
  let p = (pathname || '/').trim()
  if (!p.startsWith('/')) p = `/${p}`
  if (p.length > 1) p = p.replace(/\/+$/, '')
  return p || '/'
}

function compareSelectionStorageKeys(): string[] {
  if (typeof window === 'undefined') return []
  const keys: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key && (key === STORAGE_KEY || key.startsWith(`${STORAGE_KEY}:`))) {
      keys.push(key)
    }
  }
  return keys
}

function cleanupLegacyScopedCompareSelectionKeys(): void {
  if (typeof window === 'undefined') return
  try {
    for (const key of compareSelectionStorageKeys()) {
      if (key !== STORAGE_KEY) {
        sessionStorage.removeItem(key)
      }
    }
  } catch {
    /* ignore */
  }
}

function legacyScopedStorageKeyPath(key: string): string {
  const prefix = `${STORAGE_KEY}:`
  return key.startsWith(prefix) ? normalizeListingPath(key.slice(prefix.length)) : key
}

/** Merge legacy per-path buckets into the global key (one-time migration). */
function migrateLegacyScopedCompareSelection(): CompareProduct[] {
  const scopedKeys = compareSelectionStorageKeys()
    .filter((k) => k !== STORAGE_KEY)
    .sort((a, b) => legacyScopedStorageKeyPath(a).localeCompare(legacyScopedStorageKeyPath(b)))
  if (scopedKeys.length === 0) return []

  let merged: CompareProduct[] = []
  for (const key of scopedKeys) {
    merged = [...merged, ...parseStoredArray(sessionStorage.getItem(key))]
  }
  merged = normalizeCompareSelection(merged)
  if (merged.length > 0) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
    } catch {
      /* quota / private mode */
    }
  }
  cleanupLegacyScopedCompareSelectionKeys()
  return merged
}

export function readCompareSelection(): CompareProduct[] {
  if (typeof window === 'undefined') return []
  const global = parseStoredArray(sessionStorage.getItem(STORAGE_KEY))
  if (global.length > 0) return global
  return migrateLegacyScopedCompareSelection()
}

/** Dedupe by product id (order preserved); cap length for compare limit. */
export function normalizeCompareSelection(items: CompareProduct[], max = 4): CompareProduct[] {
  const seen = new Set<number>()
  const out: CompareProduct[] = []
  for (const p of items) {
    if (seen.has(p.id)) continue
    seen.add(p.id)
    out.push(p)
    if (out.length >= max) break
  }
  return out
}

export function writeCompareSelection(items: CompareProduct[]): void {
  if (typeof window === 'undefined') return
  const normalized = normalizeCompareSelection(items)
  try {
    if (normalized.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY)
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized))
    }
    cleanupLegacyScopedCompareSelectionKeys()
  } catch {
    /* quota / private mode */
  }
}

export function clearCompareSelection(): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.removeItem(STORAGE_KEY)
    cleanupLegacyScopedCompareSelectionKeys()
  } catch {
    /* ignore */
  }
}

/** Remove one product id from the global compare selection (and legacy buckets if any). */
export function removeProductIdFromAllCompareSelections(id: number): void {
  if (typeof window === 'undefined' || !Number.isFinite(id)) return
  try {
    writeCompareSelection(readCompareSelection().filter((p) => p.id !== id))
  } catch {
    /* quota / private mode */
  }
}

/** Clear every compare selection bucket in sessionStorage (global + legacy per-path keys). */
export function clearAllCompareSelections(): void {
  if (typeof window === 'undefined') return
  try {
    for (const key of compareSelectionStorageKeys()) {
      sessionStorage.removeItem(key)
    }
  } catch {
    /* ignore */
  }
}

export function compareSelectionHasProductId(id: number): boolean {
  if (!Number.isFinite(id) || id <= 0) return false
  return readCompareSelection().some((p) => p.id === id)
}

export function clearLegacyGlobalCompareSelection(): void {
  clearCompareSelection()
}
